import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, MailQuestion, XCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { APPLICATION_NAME } from '../constants/AppConfig';
import { parseApiError } from '../utils/errorUtils';
import AppLogo from '../images/logo.png';
import './LoginPage.css';
import './ActivatePage.css';

type ActivationStatus = 'activating' | 'success' | 'expired' | 'invalid' | 'resend';

/**
 * Landing page for the activation link emailed at company registration
 * (/activate?token=…). Without a token it shows only the resend form, which is
 * also where the login page sends users whose company is not verified yet.
 */
const ActivatePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<ActivationStatus>(token ? 'activating' : 'resend');
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  // Guard against React StrictMode double-mount firing the request twice.
  const activationStarted = useRef(false);

  useEffect(() => {
    if (!token || activationStarted.current) return;
    activationStarted.current = true;

    apiService
      .post('/companies/activate', { token })
      .then(() => setStatus('success'))
      .catch((err: unknown) => {
        setStatus(parseApiError(err).code === 'TOKEN_EXPIRED' ? 'expired' : 'invalid');
      });
  }, [token]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail.trim() || resendLoading) return;

    setResendLoading(true);
    try {
      await apiService.post('/companies/resend-activation', { email: resendEmail.trim() });
    } catch {
      // The endpoint is intentionally opaque — show the same neutral message
      // whether or not the request went through.
    } finally {
      setResendLoading(false);
      setResendSent(true);
    }
  };

  const showResendForm = status === 'expired' || status === 'invalid' || status === 'resend';

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src={AppLogo} alt={APPLICATION_NAME} className="login-logo" />
            <h1 className="login-title">{t('activate.title')}</h1>
            <p className="login-subtitle">{APPLICATION_NAME}</p>
          </div>

          {status === 'activating' && (
            <div className="activate-status" role="status">
              <Loader2 size={40} className="activate-icon activate-icon-spin" />
              <p>{t('activate.activating')}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="activate-status" role="status">
              <CheckCircle2 size={40} className="activate-icon activate-icon-success" />
              <p>{t('activate.success')}</p>
              <button type="button" className="btn-save" onClick={() => navigate('/login')}>
                {t('activate.goToLogin')}
              </button>
            </div>
          )}

          {(status === 'expired' || status === 'invalid') && (
            <div className="activate-status" role="alert">
              <XCircle size={40} className="activate-icon activate-icon-error" />
              <p>{status === 'expired' ? t('activate.expired') : t('activate.invalid')}</p>
            </div>
          )}

          {showResendForm && (
            resendSent ? (
              <div className="activate-status" role="status">
                <MailQuestion size={40} className="activate-icon activate-icon-info" />
                <p>{t('activate.resendSent')}</p>
                <button type="button" className="btn-save" onClick={() => navigate('/login')}>
                  {t('activate.goToLogin')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleResend} className="login-form" noValidate>
                <div className="form-group">
                  <label className="form-label" htmlFor="activate-email">
                    {t('activate.resendTitle')}
                  </label>
                  <input
                    id="activate-email"
                    type="email"
                    className="form-control"
                    placeholder={t('activate.resendEmailPlaceholder')}
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    disabled={resendLoading}
                    autoComplete="email"
                    maxLength={150}
                  />
                </div>
                <button type="submit" className="btn-save" disabled={resendLoading || !resendEmail.trim()}>
                  {resendLoading ? t('activate.resending') : t('activate.resend')}
                </button>
              </form>
            )
          )}

          <div className="login-footer">
            <button
              type="button"
              className="forgot-password btn btn-link p-0"
              onClick={() => navigate('/login')}
            >
              {t('register.backToLogin')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivatePage;
